import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import UserContext, { UserProfileContext } from '../../../../common/utils/getContext';
import { getData, navigateV2 } from '../../../../common/utils/router';
import css from './StructureDynamics.module.scss';
import T, { t } from '../../../../common/components/T';
import Skeleton from '../../../../widgets/plug/Skeleton/Skeleton';
import { createWidgetData, skeleton } from './helpers';
import classNames from 'classnames';
import { Img, svgSprite, Tooltip } from '../../../../hybrid/wrappers';
import CalendarFilterButton from '../CalendarFilterButton/CalendarFilterButton';
import { LangContext } from '../../../../common/decorators/Language';
import Plug from '../../../../widgets/plug/Plug/Plug';
import no_period from '../../../../public/src/img/plugs/no_chart_area_data.svg';
import HightChart from '../../../../hybrid/library/HighCharts';
import { useWindowDimensions } from '../../../../common/utils/windowDimensions';
import Slider from 'react-slick';
import { generateUniqueId } from '../../../../common/utils/utils';
import { getMemoizedData } from '../../../../common/utils/memo';

const RenderData = (props) => {
    const { chart, profileData, translation, setData } = props;
    const width = useWindowDimensions();
    const mobile = width < 768;
    const sliderReference = useRef();

    const resetFilterParams = useMemo(() => {
        if (!profileData) return '';
        const { marketing_profile: m } = profileData;
        return `filter[marketing_period_id_from]=${m.marketing_period_id || 1}&filter[marketing_period_id_to]=${
            m.marketing_period_id_current
        }`;
    }, [profileData]);

    const sliderSettings = {
        ref: sliderReference,
        arrows: false,
        dots: false,
        slidesPerRow: 3
    };

    return (
        <div className={css.block}>
            <div className={css.head}>
                <div className={css.headLeft}>
                    <p className={css.title}>
                        <T textName='structure-dynamics-office' defaultStr='Динамика развития в структуре' page='/office' />
                    </p>
                    <p className={css.period}>
                        <span className={css.periodLabel}>
                            <T textName='marketing_period' defaultStr='Маркетинговый период' page='/office' />:
                        </span>
                        <span className={css.periodValue}>
                            {chart?.period?.from_formatted === chart?.period?.to_formatted
                                ? chart?.period?.to_formatted
                                : `${chart?.period?.from_formatted} - ${chart?.period?.to_formatted}`}
                        </span>
                    </p>
                </div>
                <div className={css.headRight}>
                    <div className={css.headButtons}>
                        <CalendarFilterButton
                            url='/api/v1/user/reports/structure-qualification'
                            setData={setData}
                            attributes={['marketing_period_id_from', 'marketing_period_id_to']}
                            resetFilterParams={resetFilterParams}
                            initValue={{
                                marketing_period_id_from: {
                                    value: chart?.period?.marketing_period_id_from,
                                    label: chart?.period?.marketing_period_id_from
                                },
                                marketing_period_id_to: {
                                    value: chart?.period?.marketing_period_id_to,
                                    label: chart?.period?.marketing_period_id_to
                                }
                            }}
                            additionalButtons={[
                                {
                                    component: (
                                        <Tooltip text={<T textName='more-details' defaultStr='Подробнее' page='/office' />}>
                                            <button
                                                onClick={() => navigateV2('/reports/structure')}
                                                className={classNames('circleButton', css.headButton)}
                                            >
                                                {svgSprite('arrow-right', { className: css.buttonSvg })}
                                            </button>
                                        </Tooltip>
                                    ),
                                    mobileName: <T textName='more-details' defaultStr='Подробнее' page='/office' />,
                                    mobileFunc: () => navigateV2('/reports/structure')
                                }
                            ]}
                        />
                    </div>
                </div>
            </div>
            <div className={css.body}>
                <div className={css.allSum}>
                    <p className={css.allSumLabel}>
                        <T
                            textName='all-qualifications-in-structure'
                            defaultStr='Всего квалификаций в структуре'
                            page='/office'
                        />
                    </p>
                    <p className={css.allSumValue}>{(+chart?.sumOfRanks).toLocaleString()}</p>
                </div>
                <div className={css.ranks}>
                    {mobile ? (
                        <Slider {...sliderSettings}>
                            {chart?.ranks?.map((rank) => (
                                <div key={rank.id} className={css.rank}>
                                    <div className={css.rankIcon}>
                                        <Img
                                            src={`/src/img/ranks/rank${rank.id || 0}.png`}
                                            alt={generateUniqueId()}
                                            className={classNames(css.rankImg, `rank${rank.id}`)}
                                        />
                                    </div>
                                    <p className={css.rankName}>{rank.name}:</p>
                                    <p className={css.rankValue}>{rank?.value}</p>
                                </div>
                            ))}
                        </Slider>
                    ) : (
                        chart?.ranks?.map((rank) => (
                            <div key={rank.id} className={css.rank}>
                                <div className={css.rankIcon}>
                                    <Img
                                        src={`/src/img/ranks/rank${rank.id || 0}.png`}
                                        alt={`rank${rank.id}`}
                                        className={classNames(css.rankImg, `rank${rank.id}`)}
                                    />
                                </div>
                                <p className={css.rankName}>{rank.name}:</p>
                                <p className={css.rankValue}>{rank?.value}</p>
                            </div>
                        ))
                    )}
                </div>
                <div className={css.sliderBtns}>
                    <div
                        onClick={() => sliderReference.current.slickPrev()}
                        className={classNames('circleButton', css.sliderBtn)}
                    >
                        {svgSprite('angle-left')}
                    </div>
                    <div
                        onClick={() => sliderReference.current.slickNext()}
                        className={classNames('circleButton', css.sliderBtn)}
                    >
                        {svgSprite('angle-right')}
                    </div>
                </div>
            </div>
            {/* <div> */}
            {+chart?.sumOfRanks > 0 ? (
                <div className={css.chart}>
                    <HightChart componentConfig={chart?.chartConfig} />
                </div>
            ) : (
                <Plug
                    plugTitle={t(
                        'receivedBonusesNoPeriod_stat-plugTitle',
                        'Данных за указанный период нет',
                        '/office',
                        translation
                    )}
                    plugDescription={t(
                        'receivedBonusesNoPeriod_stat-plugDescription',
                        'Измените условия фильтрации',
                        '/office',
                        translation
                    )}
                    plugImg={no_period}
                />
            )}
            {/* </div> */}
        </div>
    );
};

export default function StructureDynamics() {
    const { translation } = useContext(LangContext);
    const { userToken, lang } = useContext(UserContext);
    const [chart, setChart] = useState(null);
    const { profileData } = useContext(UserProfileContext);
    const [periods, setPeriods] = useState([]);

    const handler = useCallback(() => {
        if (!profileData) return;
        const { marketing_profile: m } = profileData;
        getData(
            `/api/v1/user/reports/structure-qualification?filter[marketing_period_id_from]=${
                m?.marketing_period_id || 1
            }&filter[marketing_period_id_to]=${m?.marketing_period_id_current}`,
            userToken,
            lang
        ).then((res) => {
            getMemoizedData('/api/v1/periods', userToken, lang).then((res2) => {
                const periods = res2?.data?.sort((a, b) => +a.id - +b.id);
                setPeriods(periods);
                setChart(createWidgetData(res, translation, periods));
            });
        });
    }, [userToken, lang, translation, profileData]);
    return (
        <Skeleton
            wait={Boolean(chart)}
            Component={RenderData}
            componentProps={{
                chart,
                profileData,
                translation,
                setData: (res) => setChart(createWidgetData(res, translation, periods))
            }}
            pattern={skeleton}
            wrapperStyles={css.skeleton}
            lazy={true}
            handler={handler}
        />
    );
}
